import { AlertStatus } from "@models/alerts/alerts-enums";
import { getInboundFilterModel } from "@pages/alerts/utils/get-inbound-filter-model";
import { IPartialHeaderData } from "@shared/services/header.service";
import { IServerSideGetRowsParams, IServerSideGetRowsRequest } from "ag-grid-community";
import { FilterModel } from "ag-grid-enterprise";

export enum AlertsPageConfigKeyEnum {
  Inbox,
  All,
  Required,
  Postponed
}

export type IMapAlertRequestFn = (params: IServerSideGetRowsParams) => IServerSideGetRowsRequest;

export interface IAlertPageConfig {
  key: AlertsPageConfigKeyEnum;
  storageKey: string;
  pageTitle: string;
  sheetName: string;
  headerData: IPartialHeaderData;
  mapRequestFn: IMapAlertRequestFn;
}

function getRequiredAttentionFilterModel(): FilterModel {
  return {
    needAttention: {
      filterType: 'set',
      values: ['true']
    }
  };
}

function getPostponedAttentionFilterModel(): FilterModel {
  return {
    status: {
      filterType: 'set',
      values: [AlertStatus.Postponed.toString()]
    },
  };
}

function getAlertInboxConfig(): IAlertPageConfig {
  return {
    key: AlertsPageConfigKeyEnum.Inbox,
    storageKey: 'inbox-alerts',
    pageTitle: 'Inbox',
    sheetName: 'Alerts inbox',
    headerData: {
      title: 'Alerts',
      subtitle: 'Inbox',
      iconClass: 'ic-alert-triangle-20'
    },
    mapRequestFn: (params: IServerSideGetRowsParams) => {
      const request = params.request;
      const inboundFilterModel = getInboundFilterModel();

      if (request.filterModel && Object.keys(request.filterModel)?.length) {
        request.filterModel = { ...inboundFilterModel, ...request.filterModel };
      } else {
        request.filterModel = inboundFilterModel;
      }

      return request;
    }
  };
}

function getAlertAllConfig(): IAlertPageConfig {
  return {
    key: AlertsPageConfigKeyEnum.All,
    storageKey: 'all-alerts',
    pageTitle: 'All',
    sheetName: 'Alerts inbox',
    headerData: {
      title: 'Alerts',
      subtitle: 'All',
      iconClass: 'ic-alert-triangle-20'
    },
    mapRequestFn: (params: IServerSideGetRowsParams) => params.request
  };
}

function getAlertsRequireAttentionConfig(): IAlertPageConfig {
  return {
    key: AlertsPageConfigKeyEnum.Required,
    storageKey: 'require-attention-alerts',
    pageTitle: 'Require Attention',
    sheetName: 'Alerts require attention',
    headerData: {
      title: 'Alerts',
      subtitle: 'Require Attention',
      iconClass: 'ic-alert-triangle-20'
    },
    mapRequestFn: (params: IServerSideGetRowsParams) => {
      const request = params.request;
      const requiredAttentionFilterModel = getRequiredAttentionFilterModel();

      if (request.filterModel && Object.keys(request.filterModel)?.length) {
        request.filterModel = { ...requiredAttentionFilterModel, ...request.filterModel };
      } else {
        request.filterModel = requiredAttentionFilterModel;
      }

      return request;
    }
  };
}

function getAlertsPostponedConfig(): IAlertPageConfig {
  return {
    key: AlertsPageConfigKeyEnum.Postponed,
    storageKey: 'postponed-alerts',
    pageTitle: 'Postponed',
    sheetName: 'Alerts postponed',
    headerData: {
      title: 'Alerts',
      subtitle: 'Postponed',
      iconClass: 'ic-alert-triangle-20'
    },
    mapRequestFn: (params: IServerSideGetRowsParams) => {
      const request = params.request;
      const requiredAttentionFilterModel = getPostponedAttentionFilterModel();

      if (request.filterModel && Object.keys(request.filterModel)?.length) {
        request.filterModel = { ...requiredAttentionFilterModel, ...request.filterModel };
      } else {
        request.filterModel = requiredAttentionFilterModel;
      }

      return request;
    }
  };
}


const alertsPageConfigsRecord: Record<AlertsPageConfigKeyEnum, IAlertPageConfig> = {
  [AlertsPageConfigKeyEnum.Inbox]: getAlertInboxConfig(),
  [AlertsPageConfigKeyEnum.All]: getAlertAllConfig(),
  [AlertsPageConfigKeyEnum.Required]: getAlertsRequireAttentionConfig(),
  [AlertsPageConfigKeyEnum.Postponed]: getAlertsPostponedConfig(),
};

export function getAlertPageConfig(alertPageConfigKey: AlertsPageConfigKeyEnum): IAlertPageConfig {
  return alertsPageConfigsRecord[alertPageConfigKey];
}
